<template>
  <SfModalPortal
    modal-id="copy-check-list-modal"
    :size="'xs'"
    @opened="handleOpened"
  >
    <template #title>
      {{
        I18n.t('check_lists.copy_check_list_modal.title', {
          name: checkList?.name,
        })
      }}
    </template>

    <template #body>
      <div class="tw-mb-6">
        {{ I18n.t('check_lists.copy_check_list_modal.info') }}
      </div>

      <SfInput
        v-model="newChecklistName"
        :disabled="loading"
        :label="inputLabel"
        :error-text="errorMessage"
        name="new-checklist-name"
        data-testid="copy-check-list-new-name"
        :focused="true"
      />
    </template>

    <template #footer>
      <SfButtonDs :disabled="loading" @click="handleClose">
        {{ I18n.t('common.cancel') }}
      </SfButtonDs>

      <SfButtonDs
        variant="primary"
        :disabled="loading || emptyName"
        @click="submit"
      >
        {{ I18n.t('check_lists.copy_check_list_modal.submit_text') }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { SfInput } from '@app/shared/vue/components/sf-input'
import type { ModalPortalPayloadMap } from '@app/types'
import type { CopyCheckListPayload } from './types'
import * as api from '@app/application/check_lists/api'
import type { ResponseError } from '@app/utils/types'
import { SfLog } from '@app/adapters/logger'

const checkList = ref<CopyCheckListPayload>()
const newChecklistName = ref('')
const loading = ref(false)
const errorMessage = ref<string | undefined>()

const I18n = window.I18n
const inputLabel = I18n.t('check_lists.copy_check_list_modal.input_label')
const emptyName = computed(() => newChecklistName.value.length === 0)

const handleOpened = (
  payload: ModalPortalPayloadMap['copy-check-list-modal']
) => {
  checkList.value = payload
  errorMessage.value = undefined

  newChecklistName.value = [
    payload.name,
    `(${I18n.t('common.copy').toLowerCase()})`,
  ].join(' ')
}

const handleClose = () => {
  window.modalCitizen.closeModalPortal()
}

const submit = async () => {
  if (!checkList.value) {
    return
  }

  try {
    loading.value = true

    const response = await api.copyCheckList(
      checkList.value?.id,
      newChecklistName.value
    )

    if (response) {
      const { id } = response
      window.Turbolinks.visit(
        window.Silverfin.Routes.edit_check_list_path({
          firm_id: window.Silverfin.currentFirmId,
          id,
          from_copy: true,
          _options: true,
        })
      )
      handleClose()
    }
  } catch (e) {
    const error = e as ResponseError
    const { body } = error

    if (typeof body === 'string') {
      SfLog.error('Failed to copy check list', {
        error,
      })
    } else {
      const nameErrors = body.errors.name || []
      errorMessage.value = nameErrors.join(',')
    }
  } finally {
    loading.value = false
  }
}
</script>
